/**
 * `Request` module: defines the `appy`'s base types and functions.
 *
 * **Note:**: this module has been designed for internal use; please prefer the re-exported version of `appy/index`.
 *
 * @since 4.0.0
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API|Fetch Api}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/WindowOrWorkerGlobalScope/fetch|Global fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API/Using_Fetch|Using fetch}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Request|Request}
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Response|Response}
 * @see {@link https://gcanti.github.io/fp-ts/ReaderTaskEither.html|ReaderTaskEither}
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import * as E from 'fp-ts/Either';
/**
 * Return a `Req` which will be executed using `fetch()` under the hood.
 *
 * The `data` in the returned `Resp` object is of the type specified in the `type` parameter which is one of [supported `Request` methods](https://developer.mozilla.org/en-US/docs/Web/API/Response#instance_methods).
 *
 * Example:
 * ```ts
 * import {requestAs} from '@contactlab/appy';
 * import {match} from 'fp-ts/Either';
 *
 * // Default method is GET like original `fetch()`
 * const users = requestAs('json')('https://reqres.in/api/users');
 *
 * users().then(
 *   match(
 *     err => console.error(err),
 *     data => console.log(data)
 *   )
 * );
 * ```
 *
 * @category creators
 * @since 5.1.0
 */
export const requestAs = (type) => input => () => {
    const reqInput = normalizeReqInput(input);
    return fetch(...reqInput)
        .then((response) => __awaiter(void 0, void 0, void 0, function* () {
        if (!response.ok) {
            return E.left(toResponseError(new Error(`Request responded with status code ${response.status}`), response, reqInput));
        }
        const data = (yield response[type]());
        const result = { response, data, input: reqInput };
        return E.right(result);
    }))
        .catch(e => E.left(toRequestError(E.toError(e), reqInput)));
};
/**
 * Makes a request using `fetch()` under the hood.
 *
 * The `data` in the returned `Resp` object is a `string` because the response's body can **always** be converted to text without error (via [`text()`](https://developer.mozilla.org/en-US/docs/Web/API/Body/text) method).
 *
 * Example:
 * ```ts
 * import {request} from '@contactlab/appy';
 * import {match} from 'fp-ts/Either';
 *
 * // Default method is GET like original `fetch()`
 * const users = request('https://reqres.in/api/users');
 *
 * users().then(
 *   match(
 *     err => console.error(err),
 *     data => console.log(data)
 *   )
 * );
 * ```
 *
 * @category creators
 * @since 4.0.0
 */
export const request = requestAs('text');
/**
 * Creates a `RequestError` object.
 *
 * @category Error
 * @since 4.0.0
 */
export const toRequestError = (error, input) => ({ type: 'RequestError', error, input });
/**
 * Creates a `ResponseError` object.
 *
 * @category Error
 * @since 4.0.0
 */
export const toResponseError = (error, response, input) => ({ type: 'ResponseError', response, error, input });
/**
 * Normalizes the input of a `Req` to a `RequestInfoInit` tuple even when only a single `RequestInfo` is provided.
 *
 * @category Request
 * @since 4.0.0
 */
export const normalizeReqInput = (input) => Array.isArray(input) ? input : [input, {}];
//# sourceMappingURL=request.js.map